import { createContext, use, useCallback, useEffect, useState, type ReactNode } from 'react';
import type { AssistantBrief } from './prompt';

/**
 * How a page hands the floating assistant its context: `useAssistantPage({ id, title, brief })`.
 * The shell reads whichever page is current. A page that never calls it isn't wired up.
 */
export interface AssistantPage {
  /** Keys the server's per-page instructions (`pages.<id>`). */
  id: string;
  title: string;
  /** Null while the page's figures load, or while it has none to explain (`pending` says why). */
  brief: AssistantBrief | null;
  pending?: string;
}

type Publish = (token: object, page: AssistantPage | null) => void;

// Two contexts: pages only publish (a stable function, so they never re-render on a change), and
// only the assistant reads the current page.
const PublishContext = createContext<Publish | null>(null);
const PageContext = createContext<AssistantPage | null>(null);

export function AssistantPageProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<{ token: object; page: AssistantPage } | null>(null);
  // A page that unmounts clears only its own entry: the next page may already have published.
  const publish = useCallback<Publish>(
    (token, page) => setEntry((prev) => (page ? { token, page } : prev?.token === token ? null : prev)),
    [],
  );
  return (
    <PublishContext value={publish}>
      <PageContext value={entry?.page ?? null}>{children}</PageContext>
    </PublishContext>
  );
}

/** The page the assistant should explain, or null on a page that isn't wired up. */
export const useCurrentAssistantPage = () => use(PageContext);

/** Publishes this page to the assistant while it's mounted. A no-op outside the app shell. */
export function useAssistantPage(page: AssistantPage) {
  const publish = use(PublishContext);
  const [token] = useState(() => ({}));
  const { id, title, brief, pending } = page;
  const text = brief?.text;
  const refs = brief?.refs.join(' ');
  useEffect(() => {
    publish?.(token, {
      id,
      title,
      brief: text === undefined ? null : { text, refs: refs ? refs.split(' ') : [] },
      pending,
    });
  }, [publish, token, id, title, text, refs, pending]);
  useEffect(() => () => publish?.(token, null), [publish, token]);
}
